import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Modal, Form, Alert, Card, Badge } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import axios from 'axios';
import ActionButton, { ActionButtonGroup } from '../../../components/common/ActionButton';
import './AdminBeneficios.css';

const AdminBeneficios = () => {
  const [beneficios, setBeneficios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showInstructivo, setShowInstructivo] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewBeneficio, setPreviewBeneficio] = useState(null);
  
  // Normalizar API_URL (remover barra final si existe)
  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

  useEffect(() => {
    cargarBeneficios();
  }, []);

  const cargarBeneficios = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      const token = localStorage.getItem('token');
      
      if (!user || !token) {
        setError('Usuario no autenticado');
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/beneficios`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-user-id': String(user.id || user.Legajo || user.CdEmpleado || ''),
          'x-user-role': String(user.IdRol || user.tipoUsuario || '1')
        }
      });
      
      // Manejar respuesta (puede ser array directo o {success, data})
      const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
      
      // Construir URLs completas desde el backend
      const beneficiosConUrl = data.map(b => ({
        ...b,
        url: b.url?.startsWith('http') ? b.url : `${API_URL}${b.url}` // Completar URL si es relativa
      }));
      
      setBeneficios(beneficiosConUrl);
    } catch (err) {
      setError('Error al cargar beneficios: ' + (err.response?.data?.error || err.message));
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Preview para imágenes
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
      
      // Mostrar info de archivo seleccionado
      console.log('Archivo seleccionado:', file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Debe seleccionar un archivo');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      const token = localStorage.getItem('token');
      
      if (!user || !token) {
        setError('Usuario no autenticado');
        return;
      }
      
      const uploadData = new FormData();
      uploadData.append('beneficio', selectedFile);
      
      const response = await axios.post(`${API_URL}/api/beneficios/upload`, uploadData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          'x-user-id': String(user.id || user.Legajo || user.CdEmpleado || ''),
          'x-user-role': String(user.IdRol || user.tipoUsuario || '1')
        }
      });
      
      setSuccess(`✅ ${response.data.mensaje}`);
      setShowModal(false);
      
      // Resetear formulario
      setSelectedFile(null);
      setPreviewUrl(null);
      
      // Recargar lista
      await cargarBeneficios();
    } catch (err) {
      setError('Error al subir beneficio: ' + (err.response?.data?.error || err.message));
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (numero, nombreArchivo) => {
    if (!window.confirm(`¿Está seguro de eliminar "${nombreArchivo}"?\n\nNota: Los archivos posteriores se renombrarán automáticamente.`)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      const token = localStorage.getItem('token');
      
      if (!user || !token) {
        setError('Usuario no autenticado');
        return;
      }
      
      const response = await axios.delete(`${API_URL}/api/beneficios/${numero}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-user-id': String(user.id || user.Legajo || user.CdEmpleado || ''),
          'x-user-role': String(user.IdRol || user.tipoUsuario || '1')
        }
      });
      
      setSuccess(response.data.mensaje);
      await cargarBeneficios();
    } catch (err) {
      setError('Error al eliminar beneficio: ' + (err.response?.data?.error || err.message));
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (numero, direccion) => {
    setLoading(true);
    setError(null);
    
    try {
      const storedUser = localStorage.getItem('user');
      const user = storedUser ? JSON.parse(storedUser) : null;
      const token = localStorage.getItem('token');
      
      if (!user || !token) {
        setError('Usuario no autenticado');
        return;
      }
      
      const response = await axios.put(`${API_URL}/api/beneficios/${numero}/mover`, 
        { direccion },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-user-id': String(user.id || user.Legajo || user.CdEmpleado || ''),
            'x-user-role': String(user.IdRol || user.tipoUsuario || '1')
          }
        }
      );
      
      setSuccess(response.data.mensaje);
      await cargarBeneficios();
    } catch (err) {
      setError('Error al mover beneficio: ' + (err.response?.data?.error || err.message));
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (beneficio) => {
    setPreviewBeneficio(beneficio);
    setShowPreviewModal(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / 1048576).toFixed(2) + ' MB';
  };

  return (
    <Container fluid className="admin-beneficios">
      <Row className="mb-4">
        <Col>
          <h2>Gestión de Beneficios</h2>
          <p className="text-muted">
            Administre los beneficios que se muestran a los empleados
          </p>
        </Col>
        <Col xs="auto">
          <ActionButton 
            variant="add"
            onClick={() => setShowModal(true)}
            disabled={loading}
          >
            Nuevo Beneficio
          </ActionButton>
        </Col>
      </Row>

      {/* Instructivo */}
      {showInstructivo && (
        <Alert variant="info" dismissible onClose={() => setShowInstructivo(false)}>
          <Alert.Heading>
            <FaInfoCircle /> Formato de archivos
          </Alert.Heading>
          <p className="mb-0">
            <strong>Los archivos se guardarán automáticamente con el formato:</strong> 
            <code className="ms-2">beneficio1.jpg</code>, <code>beneficio2.pdf</code>, etc.
          </p>
          <hr />
          <ul className="mb-0">
            <li>Puede subir archivos con cualquier nombre, se renombrarán automáticamente</li>
            <li>Formatos permitidos: <Badge bg="success">JPG</Badge> <Badge bg="success">PNG</Badge> <Badge bg="success">PDF</Badge></li>
            <li>Tamaño máximo: <Badge bg="warning">10 MB</Badge></li>
            <li>Al eliminar un beneficio, los siguientes se reorganizarán automáticamente</li>
          </ul>
        </Alert>
      )}

      {/* Alertas */}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Tabla de beneficios */}
      <Card>
        <Card.Body>
          {loading && beneficios.length === 0 ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
            </div>
          ) : beneficios.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>No hay beneficios cargados</p>
              <Button variant="primary" onClick={() => setShowModal(true)}>
                Cargar primer beneficio
              </Button>
            </div>
          ) : (
            <Table hover responsive>
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>Orden</th>
                  <th>Archivo</th>
                  <th>Tipo</th>
                  <th>Tamaño</th>
                  <th>Fecha modificación</th>
                  <th style={{ width: '200px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {beneficios.map((beneficio, index) => (
                  <tr key={beneficio.id}>
                    <td className="text-center">
                      <Badge bg="secondary">{beneficio.orden}</Badge>
                    </td>
                    <td>
                      <code>{beneficio.archivo}</code>
                    </td>
                    <td>
                      {beneficio.tipo === 'pdf' ? (
                        <Badge bg="danger">PDF</Badge>
                      ) : (
                        <Badge bg="success">Imagen</Badge>
                      )}
                    </td>
                    <td>{formatFileSize(beneficio.tamanio)}</td>
                    <td>{new Date(beneficio.fecha_modificacion).toLocaleString('es-AR')}</td>
                    <td>
                      <ActionButtonGroup>
                        {/* Botón preview */}
                        <ActionButton
                          variant="view"
                          tooltip="Ver beneficio"
                          onClick={() => handlePreview(beneficio)}
                          size="sm"
                        />
                        
                        {/* Botones de ordenamiento */}
                        <ActionButton
                          variant="info"
                          tooltip="Mover arriba"
                          onClick={() => handleReorder(beneficio.orden, 'arriba')}
                          disabled={index === 0 || loading}
                          size="sm"
                        >
                          ↑
                        </ActionButton>
                        
                        <ActionButton
                          variant="info"
                          tooltip="Mover abajo"
                          onClick={() => handleReorder(beneficio.orden, 'abajo')}
                          disabled={index === beneficios.length - 1 || loading}
                          size="sm"
                        >
                          ↓
                        </ActionButton>
                        
                        {/* Botón eliminar */}
                        <ActionButton
                          variant="delete"
                          tooltip="Eliminar beneficio"
                          onClick={() => handleDelete(beneficio.orden, beneficio.archivo)}
                          disabled={loading}
                          size="sm"
                        />
                      </ActionButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para subir beneficio */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Subir Nuevo Beneficio</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Alert variant="warning">
              <strong>Nota:</strong> El archivo se guardará automáticamente como <code>beneficio{beneficios.length + 1}.{selectedFile ? selectedFile.name.split('.').pop() : 'ext'}</code>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label>Seleccionar archivo</Form.Label>
              <Form.Control
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileChange}
                required
              />
              <Form.Text className="text-muted">
                Formatos: JPG, PNG, PDF (máx. 10 MB)
              </Form.Text>
            </Form.Group>

            {previewUrl && (
              <div className="text-center mb-3">
                <p className="text-muted">Vista previa:</p>
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  style={{ maxWidth: '100%', maxHeight: '300px', border: '1px solid #ddd', borderRadius: '4px' }} 
                />
              </div>
            )}

            {selectedFile && !previewUrl && (
              <Alert variant="info">
                <strong>Archivo seleccionado:</strong> {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading || !selectedFile}>
              {loading ? 'Subiendo...' : 'Subir Beneficio'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal preview */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Vista Previa - {previewBeneficio?.archivo}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewBeneficio && (
            previewBeneficio.tipo === 'pdf' ? (
              <div>
                <p>Para ver el PDF completo, haga clic en el botón de abajo:</p>
                <a 
                  href={previewBeneficio.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Abrir PDF en nueva pestaña
                </a>
              </div>
            ) : (
              <img 
                src={previewBeneficio.url} 
                alt={previewBeneficio.archivo}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminBeneficios;
